import axios, { AxiosRequestConfig } from 'axios'

import type { CommonChartConfig } from '../contexts/common'
import { commonChartOptions } from '../contexts/common'
import type { BaseContext } from '../contexts/index'
import {
  defaultMSDIndicators,
  defaultMinMaxIndicators,
  range6m1yAll,
} from '../utils/config'
import { MONTH_LIST_SHORT, chartPalette } from '../utils/constants'
import { DEFAULT_TIMEOUT_MS } from './constants'
import { mergeDeepAll } from './'

export type StatusFlag = 'red' | 'yellow' | 'green'

export interface CompassAPIDataPoint {
  date: string
  value: number
}

export interface CompassAPIDispersionDataPoint {
  x: number
  y: number
  label?: string
  symbol?: string | number
}

export interface CompassAPIResultTradingAttDataPoint {
  result_date: number
  trading_att: number
}

export interface CompassAPIGiroDataPoint {
  fund: string
  giro: number
}

export interface CompassAPIMultipleDataPoint {
  date: string
  metric: number
  weight: number
}

export interface CompassAPIRatioDataPoint {
  date: string
  ratio: number
  spread: number
}

export interface CompassAPIDataBase {
  data_sources: DataSource[] | null
  status_flag?: StatusFlag
}

export interface CompassAPIMultiSeriesChartData extends CompassAPIDataBase {
  title: string
  subtitle?: string
  data: {
    name: string
    id?: string
    data: CompassAPIDataPoint[]
  }[]
}

export interface DataSource {
  text: string
  href: string
  last_capture: string | null
  status_flag?: StatusFlag
}

export interface CompassAPIMultipleChartData extends CompassAPIDataBase {
  data: {
    id: string
    values: CompassAPIMultipleDataPoint[]
  }[]
}

export interface CompassBasketValuationAPIRatioMultipleChartData
  extends CompassAPIDataBase {
  data: {
    name: string
    first_metric: CompassAPIDataPoint[]
    second_metric: CompassAPIDataPoint[]
    ratio: CompassAPIDataPoint[]
    spread: CompassAPIDataPoint[]
  }[]
}

export interface CompassAPIDispersionChartData extends CompassAPIDataBase {
  title?: string
  data: {
    name: string
    values: CompassAPIDispersionDataPoint[]
  }[]
}

export interface xAxis {
  dateTimeLabelFormats?: {
    millisecond?: string
    second?: string
    minute?: string
    hour?: string
    day?: string
    week?: string
    month?: string
    year?: string
  }
  type?: string
  categories?: string[]
  id?: string
  title?: {
    align?: string
    offset?: number
    text?: string
  }
  labels?: {
    format?: string
    formatter?: () => string
    rotation?: number
    step?: number
    x?: number
  }
  max?: number
  min?: number
  tickPositions?: number[]
  xDateFormat?: string
}

interface PlotLine {
  color: string
  dashStyle: string
  label: {
    align: string
    text: string
  }
  value: number
  width: number
  zIndex: number
}

interface RangeSelector {
  selected?: number
  buttons: {
    type: string
    count?: number
    text: string
  }[]
}

export interface yAxis {
  height?: string
  id?: string
  labelSuffix?: string
  max?: number
  min?: number
  offset?: number
  opposite?: boolean
  plotLines?: PlotLine[]
  resize?: {
    enabled: boolean
  }
  title?: {
    text?: string
  }
  type?: string
  top?: string
  reversed?: boolean
  startOnTick?: boolean
  endOnTick?: boolean
}

interface Magnitude {
  value: number
  symbol: string
}

interface TooltipFormatterScatterOptions {
  disableXAxisLabel?: boolean
  xAxisSuffix?: string
  yAxisSuffix?: string
  xAxisLabel?: string
  yAxisLabel?: string
  decimals?: number
  unit?: string
}

interface TooltipFormatterScatter {
  color?: string
  x: number
  y: number
  point: {
    name: string | undefined
    label: string | undefined
  }
  series?: {
    color: string | undefined
    name: string | undefined
  }
}

interface SubtitleStyle {
  fontSize: string
  color: string
}

interface Subtitle {
  style?: SubtitleStyle
  text: string | undefined
}

export const getCompassAPIChartDataRequestConfig = (
  config: CommonChartConfig
) =>
  ({
    timeout: DEFAULT_TIMEOUT_MS,
    url: config.url,
    method: 'GET',
  } as AxiosRequestConfig)

export const getCompassAPIChartData = async (
  config: CommonChartConfig,
  requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
    conf
  ) => conf
) => {
  const response = await axios(
    requestModifier(getCompassAPIChartDataRequestConfig(config))
  )
  return response.data
}

export const convertCompassToHighchartsDataFormat = (
  data: CompassAPIDataPoint[],
  options?: { convertPercentage?: boolean }
) =>
  data.map(({ date, value }) => [
    new Date(date).getTime(),
    options?.convertPercentage ? 100 * value : value,
  ])

export const convertCompassToHighchartsStrCategoryFormat = (
  data: CompassAPIDataPoint[],
  options?: { convertPercentage?: boolean }
) =>
  data.map(({ date, value }) => [
    date,
    options?.convertPercentage ? 100 * value : value,
  ])

export const convertCompassValuationMultipleToHighchartsDataFormat = (
  data: CompassAPIMultipleDataPoint[],
  options?: { convertPercentage?: boolean }
) =>
  data.map(({ date, metric }) => [
    new Date(date).getTime(),
    options?.convertPercentage ? 100 * metric : metric,
  ])

export const convertCompassMultipleToHighchartsFormat = (
  data: CompassAPIMultipleDataPoint[],
  options?: { convertPercentage?: boolean }
) =>
  data.map(({ date, metric }) => [
    new Date(date).getTime(),
    options?.convertPercentage ? 100 * metric : metric,
  ])

export const convertCompassRatioToHighchartsFormat = (
  data: CompassAPIRatioDataPoint[],
  options?: { convertPercentage?: boolean }
) =>
  data.map(({ date, ratio }) => [
    new Date(date).getTime(),
    options?.convertPercentage ? 100 * ratio : ratio,
  ])

const filterByIndex = (arr: any[], indexes?: number[]) =>
  arr.filter((_, idx) => indexes?.some((element) => idx === element) ?? true)

const getSubtitleConfig = (config: Subtitle) => {
  const { style, text } = config
  return text
    ? {
        subtitle: {
          style: {
            fontSize: style?.fontSize ?? '16px',
            color: style?.color ?? '#000',
          },
          text: text,
        },
      }
    : {}
}

export const getMultipleLineChartProps = <T, U, V>(
  context: BaseContext<T, U, V>,
  options?: {
    addMinMaxIndicators?: boolean
    addMinMaxIndicatorsIndexes?: number[]
    chartTitle?: string
    chartSubtitle?: string
    convertToPercentages?: boolean[]
    legend?: {
      maxHeight?: number
    }
    msdIndicatorsSeriesIndexes?: number[]
    msdIndicators?: { avg: boolean; sd1: boolean; sd2: boolean }
    rangeSelector?: RangeSelector
    tooltip?: {
      formatter?: () => string
      pointFormatter?: () => string
      pointFormat?: string
      shared?: boolean
      split?: boolean
      xDateFormat?: string
    }
    xAxis?: xAxis
    yAxis?: yAxis[]
    useRegularChart?: boolean
    stockTools?: boolean
    categorical?: boolean
    enableMarker?: boolean
  },
  seriesOptions?: {
    color?: string[]
    dashStyle?: string[]
    enableMouseTracking?: boolean[]
    lineWidth?: number[]
    name?: string[]
    opacity?: number[]
    showInLegend?: boolean[]
    stack?: string[]
    type?: string[]
    tooltip?: {
      pointFormatter?: () => string
    }[]
    boostThreshold?: number[]
    visible?: boolean[]
    yAxis?: string[]
    ids?: string[]
    linkedTo?: (string | undefined)[]
  }
) => (config: U, data: CompassAPIMultiSeriesChartData) => {
  const series = data.data.map((s, idx) => ({
    color: seriesOptions?.color?.[idx] ?? chartPalette[idx],
    dashStyle: seriesOptions?.dashStyle?.[idx] ?? 'Solid',
    enableMouseTracking: seriesOptions?.enableMouseTracking?.[idx] ?? true,
    id: seriesOptions?.ids?.[idx] ?? `series-${idx}`,
    linkedTo: seriesOptions?.linkedTo?.[idx],
    lineWidth: seriesOptions?.lineWidth?.[idx] ?? 2,
    name: seriesOptions?.name?.[idx] ?? s.name ?? undefined,
    opacity: seriesOptions?.opacity?.[idx] ?? true,
    showInLegend: seriesOptions?.showInLegend?.[idx] ?? true,
    stack: seriesOptions?.stack?.[idx] ?? undefined,
    type: seriesOptions?.type?.[idx] ?? 'line',
    visible: seriesOptions?.visible?.[idx] ?? true,
    yAxis: seriesOptions?.yAxis?.[idx] ?? 'default',
    marker: { enabled: options?.enableMarker },
    tooltip: {
      ...(options?.convertToPercentages?.[idx] ? { valueSuffix: '%' } : {}),
      ...(seriesOptions?.tooltip?.[idx]?.pointFormatter
        ? { pointFormatter: seriesOptions?.tooltip?.[idx]?.pointFormatter }
        : {}),
      valueDecimals: 2,
    },
    ...(seriesOptions?.boostThreshold
      ? { boostThreshold: seriesOptions?.boostThreshold?.[idx] }
      : {}),
    data: options?.categorical
      ? convertCompassToHighchartsStrCategoryFormat(s.data, {
          convertPercentage: options?.convertToPercentages?.[idx] ?? false,
        })
      : convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: options?.convertToPercentages?.[idx] ?? false,
        }),
  }))

  const xAxis = {
    ...(options?.xAxis?.dateTimeLabelFormats
      ? { dateTimeLabelFormats: options.xAxis.dateTimeLabelFormats }
      : {}),
    labels: {
      format: options?.xAxis?.labels?.format ?? undefined,
      rotation: options?.xAxis?.labels?.rotation ?? undefined,
    },
    tickPositions: options?.xAxis?.tickPositions ?? 0,
    type: options?.xAxis?.type,
    categories: options?.xAxis?.categories,
  }

  const yAxis = options?.yAxis
    ? options.yAxis.map((yAxis) => ({
        height: yAxis.height ?? undefined,
        id: yAxis.id ?? 'default',
        offset: yAxis.offset ?? undefined,
        opposite: yAxis.opposite ?? 0,
        min: yAxis.min ?? undefined,
        reversed: yAxis.reversed ?? false,
        startOnTick: yAxis.startOnTick ?? false,
        endOnTick: yAxis.endOnTick ?? false,
        ...(yAxis?.plotLines ? { plotLines: yAxis.plotLines } : {}),
        ...(yAxis.resize ? { resize: { enabled: yAxis.resize.enabled } } : {}),
        title: yAxis.title ? { text: yAxis.title?.text ?? '' } : {},
        type: yAxis.type ?? 'linear',
        top: yAxis.top ?? undefined,
        ...(yAxis?.labelSuffix
          ? { labels: { format: `{value}${yAxis.labelSuffix}` } }
          : {}),
      }))
    : [
        {
          id: 'default',
          opposite: false,
          startOnTick: false,
          endOnTick: false,
          ...(options?.convertToPercentages
            ? options.convertToPercentages[0]
              ? { labels: { format: '{value}%' } }
              : {}
            : {}),
        },
      ]

  const tooltip = {
    formatter: options?.tooltip?.formatter ?? undefined,
    pointFormatter: options?.tooltip?.pointFormatter ?? undefined,
    shared: options?.tooltip?.shared ?? false,
    split: options?.tooltip?.split ?? true,
    xDateFormat: options?.tooltip?.xDateFormat ?? '%A, %b %e, %Y',
  }

  const legend = options?.legend && {
    legend: { maxHeight: options?.legend?.maxHeight },
  }

  return {
    id: context.getChartId(config),
    title: options?.chartTitle ?? data.title,
    defaultIndicators: [
      ...(options?.addMinMaxIndicators
        ? defaultMinMaxIndicators(
            filterByIndex(series, options.addMinMaxIndicatorsIndexes)
          )
        : []),
      ...(options?.msdIndicators
        ? defaultMSDIndicators(
            filterByIndex(series, options.msdIndicatorsSeriesIndexes),
            options.msdIndicators
          )
        : []),
    ],
    options: mergeDeepAll([
      commonChartOptions,
      legend ?? {},
      getDataSources(data.data_sources),
      getSubtitleConfig({ text: options?.chartSubtitle ?? data.subtitle }),
      {
        rangeSelector: options?.rangeSelector ?? range6m1yAll,
        series,
        tooltip,
        xAxis,
        yAxis,
      },
    ]),
    stockTools: options?.stockTools ?? true,
    useRegularChart: !!options?.useRegularChart,
  }
}

export const getMultipleColumnChartProps = <T, U, V>(
  context: BaseContext<T, U, V>,
  options?: {
    addMinMaxIndicators?: boolean
    addMinMaxIndicatorsIndexes?: number[]
    chartTitle?: string
    chartSubtitle?: string
    convertToPercentages?: boolean[]
    legend?: {
      maxHeight?: number
    }
    msdIndicators?: { avg: boolean; sd1: boolean; sd2: boolean }
    plotOptions?: {
      series?: {
        pointWidth?: number
      }
      column?: {
        borderRadius?: number
        clip?: boolean
        grouping?: boolean
        groupPadding?: number
        pointPadding?: number
        minPointLength?: number
        pointPlacement?: string
        pointWidth?: number
        stacking?: string
      }
      area?: {
        stacking?: string
      }
    }
    tooltip?: {
      formatter?: () => string
      pointFormatter?: () => string
      pointFormat?: string
      shared?: boolean
      split?: boolean
      xDateFormat?: string
    }
    xAxis?: xAxis
    yAxis?: yAxis[]
  },
  seriesOptions?: {
    color?: string[]
    dashStyle?: string[]
    enableMouseTracking?: boolean[]
    lineWidth?: number[]
    name?: string[]
    opacity?: number[]
    showInLegend?: boolean[]
    stack?: string[]
    tooltip?: {
      pointFormatter?: () => string
    }[]
    type?: string[]
    visible?: boolean[]
    yAxis?: string[]
    zIndex?: number[]
  }
) => (config: U, data: CompassAPIMultiSeriesChartData) => {
  const series = data.data.map((s, idx) => ({
    color: seriesOptions?.color?.[idx] ?? chartPalette[idx],
    data: convertCompassToHighchartsDataFormat(s.data, {
      convertPercentage: options?.convertToPercentages?.[idx] ?? false,
    }),
    enableMouseTracking: seriesOptions?.enableMouseTracking?.[idx] ?? true,
    id: `series-${idx}`,
    lineWidth: seriesOptions?.lineWidth?.[idx] ?? 2,
    name: seriesOptions?.name?.[idx] ?? s.name ?? undefined,
    opacity: seriesOptions?.opacity?.[idx] ?? true,
    showInLegend: seriesOptions?.showInLegend?.[idx] ?? true,
    stack: seriesOptions?.stack?.[idx] ?? undefined,
    type: seriesOptions?.type?.[idx] ?? 'column',
    visible: seriesOptions?.visible?.[idx] ?? true,
    yAxis: seriesOptions?.yAxis?.[idx] ?? 'default',
    zIndex: seriesOptions?.zIndex?.[idx] ?? undefined,
    tooltip: {
      ...(options?.convertToPercentages?.[idx] ? { valueSuffix: '%' } : {}),
      ...(seriesOptions?.tooltip?.[idx]?.pointFormatter
        ? { pointFormatter: seriesOptions?.tooltip?.[idx]?.pointFormatter }
        : {}),
      valueDecimals: 2,
    },
  }))
  const plotOptions = options?.plotOptions
    ? {
        column: {
          borderRadius: 0,
          clip: true,
          grouping: true,
          groupPadding: 0.2,
          pointPadding: 0.1,
          minPointLength: 2,
          centerInCategory: true,
          ...(options?.plotOptions?.column ?? {}),
        },
        area: {
          ...(options?.plotOptions?.area ?? {}),
        },
      }
    : {}

  const yAxis = options?.yAxis
    ? options.yAxis.map((yAxis) => ({
        id: yAxis.id ?? 'default',
        offset: yAxis.offset ?? 0,
        opposite: yAxis.opposite ?? false,
        min: yAxis.min ?? undefined,
        startOnTick: yAxis.startOnTick ?? false,
        endOnTick: yAxis.endOnTick ?? false,
        title: yAxis.title
          ? {
              text: yAxis.title?.text ?? '',
            }
          : {},
        ...(yAxis?.labelSuffix
          ? { labels: { format: `{value}${yAxis.labelSuffix}` } }
          : {}),
      }))
    : [
        {
          id: 'default',
          offset: options?.yAxis?.[0].offset ?? 0,
          opposite: false,
          startOnTick: false,
          endOnTick: false,
          ...(options?.convertToPercentages?.[0]
            ? { labels: { format: '{value}%' } }
            : {}),
        },
      ]

  const xAxis = {
    ...(options?.xAxis?.dateTimeLabelFormats
      ? { dateTimeLabelFormats: options.xAxis.dateTimeLabelFormats }
      : {}),
    labels: {
      format: options?.xAxis?.labels?.format ?? undefined,
      rotation: options?.xAxis?.labels?.rotation ?? undefined,
    },
    tickPositions: options?.xAxis?.tickPositions ?? 0,
  }

  const tooltip = {
    formatter: options?.tooltip?.formatter ?? undefined,
    pointFormatter: options?.tooltip?.pointFormatter ?? undefined,
    shared: options?.tooltip?.shared ?? false,
    split: options?.tooltip?.split ?? true,
    xDateFormat: options?.tooltip?.xDateFormat ?? '%A, %b %e, %Y',
  }

  const legend = options?.legend && {
    legend: { maxHeight: options?.legend?.maxHeight },
  }

  return {
    id: context.getChartId(config),
    title: options?.chartTitle ?? data.title,
    defaultIndicators: [
      ...(options?.addMinMaxIndicators
        ? defaultMinMaxIndicators(
            filterByIndex(series, options.addMinMaxIndicatorsIndexes)
          )
        : []),
      ...(options?.msdIndicators
        ? defaultMSDIndicators(series, options.msdIndicators)
        : []),
    ],
    options: mergeDeepAll([
      commonChartOptions,
      legend ?? {},
      getDataSources(data.data_sources),
      getSubtitleConfig({ text: options?.chartSubtitle ?? data.subtitle }),
      {
        plotOptions,
        rangeSelector: range6m1yAll,
        series,
        tooltip,
        xAxis,
        yAxis,
      },
    ]),
    stockTools: true,
  }
}

export const getMultipleLineStrCategoriesChartProps = <T, U, V>(
  context: BaseContext<T, U, V>,
  options?: {
    chartTitle?: string
    addMinMaxIndicators?: boolean
    addMinMaxIndicatorsIndexes?: number[]
    msdIndicators?: { avg: boolean; sd1: boolean; sd2: boolean }
    convertToPercentages?: boolean[]
    tooltip?: {
      formatter?: () => string
      pointFormatter?: () => string
      shared?: boolean
      split?: boolean
    }
    forceXAxisCategories?: boolean
    xAxisCategories?: string[]
    yAxis?: yAxis[]
  },
  seriesOptions?: {
    color?: string[]
    lineWidth?: number[]
    name?: string[]
    tooltip?: {
      pointFormatter?: () => string
    }[]
    type?: string[]
    zIndex?: number[]
    yAxis?: string[]
  }
) => (config: U, data: CompassAPIMultiSeriesChartData) => {
  const series = data.data.map((s, idx) => ({
    color: seriesOptions?.color?.[idx] ?? chartPalette[idx],
    id: `series-${idx}`,
    type: seriesOptions?.type?.[idx] ?? 'line',
    marker: { enabled: false },
    name: seriesOptions?.name?.[idx] ?? s.name ?? undefined,
    lineWidth: seriesOptions?.lineWidth?.[idx] ?? 2,
    yAxis: seriesOptions?.yAxis?.[idx] ?? 'default',
    zIndex: seriesOptions?.zIndex?.[idx] ?? undefined,
    tooltip: {
      ...(options?.convertToPercentages?.[idx] ? { valueSuffix: '%' } : {}),
      ...(seriesOptions?.tooltip?.[idx]?.pointFormatter
        ? { pointFormatter: seriesOptions?.tooltip?.[idx]?.pointFormatter }
        : {}),
      valueDecimals: 2,
    },
    data: convertCompassToHighchartsStrCategoryFormat(s.data, {
      convertPercentage: options?.convertToPercentages?.[idx] ?? false,
    }),
  }))

  const yAxis = options?.yAxis
    ? options.yAxis.map((yAxis) => ({
        id: yAxis.id ?? 'default',
        opposite: yAxis.opposite ?? false,
        min: yAxis.min ?? undefined,
        startOnTick: yAxis.startOnTick ?? false,
        endOnTick: yAxis.endOnTick ?? false,
        title: yAxis.title
          ? {
              text: yAxis.title?.text ?? '',
            }
          : { text: '' },
        ...(yAxis?.labelSuffix
          ? { labels: { format: `{value}${yAxis.labelSuffix}` } }
          : {}),
      }))
    : [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          startOnTick: false,
          endOnTick: false,
          ...(options?.convertToPercentages
            ? options.convertToPercentages[0]
              ? { labels: { format: '{value}%' } }
              : {}
            : {}),
        },
      ]

  const xAxis = [
    {
      type: 'category',
      categories:
        options?.forceXAxisCategories ?? true
          ? options?.xAxisCategories ?? MONTH_LIST_SHORT
          : undefined,
    },
  ]

  const tooltip = {
    formatter: options?.tooltip?.formatter ?? undefined,
    pointFormatter: options?.tooltip?.pointFormatter ?? undefined,
    shared: options?.tooltip?.shared ?? false,
    split: options?.tooltip?.split ?? true,
  }

  return {
    id: context.getChartId(config),
    title: options?.chartTitle ?? data.title,
    defaultIndicators: [
      ...(options?.addMinMaxIndicators
        ? defaultMinMaxIndicators(
            filterByIndex(series, options.addMinMaxIndicatorsIndexes)
          )
        : []),
      ...(options?.msdIndicators
        ? defaultMSDIndicators(series, options.msdIndicators)
        : []),
    ],
    options: mergeDeepAll([
      commonChartOptions,
      getDataSources(data.data_sources),
      {
        rangeSelector: range6m1yAll,
        series,
        tooltip,
        xAxis,
        yAxis,
      },
    ]),
    useRegularChart: true,
    stockTools: true,
  }
}

export const getAreaRangeData = (
  values: Record<number, { min?: number; max?: number }> = {}
) =>
  Object.entries(values)
    .filter(
      (value): value is [string, { min: number; max: number }] =>
        value[1].min !== undefined && value[1].max !== undefined
    )
    .map(([x, { min, max }]) => [parseInt(x), min, max])
    .sort(([xa], [xb]) => xa - xb)

export const tooltipFormatterMinMaxMonthly = (options?: {
  pointKeysAreTimestamps?: boolean
  valueSuffix?: string
}) =>
  function (this: {
    points: {
      y: number
      key: string
      series: {
        name: string
        color: string
      }
    }[]
  }) {
    const sortedPoints = [...this.points].sort((pa, pb) => pb.y - pa.y)
    const minY = sortedPoints[sortedPoints.length - 1].y
    const maxY = sortedPoints[0].y
    const points = sortedPoints.map((point: any) => {
      const isMin = point.y === minY
      const isMax = point.y === maxY
      return `
      <span style="color: ${point.series.color}">●</span>
      ${point.series.name}:
      <span style="font-weight: bold">
        ${point.y.toFixed(1)}${options?.valueSuffix ?? ''}${
        isMax ? ' (max)' : isMin ? ' (min)' : ''
      }
      </span>
    `
    })

    return `
    <span>${
      options?.pointKeysAreTimestamps
        ? MONTH_LIST_SHORT[new Date(sortedPoints[0].key).getMonth()]
        : sortedPoints[0].key
    }</span>
    <br/>
    ${points.join('<br/>')}
  `
  }

export const tooltipFormatterWithLabel = () =>
  function () {
    // @ts-ignore
    const _this = this as {
      index: number
      y: number
      series: { options: { pointLabels: string[] } }
    }

    return `<b>${_this.y.toFixed(2)}</b><br/>${
      _this.series.options.pointLabels[_this.index]
    }`
  }

export const tooltipFormatterScatter = (
  options?: TooltipFormatterScatterOptions
) =>
  function (this: TooltipFormatterScatter) {
    const {
      disableXAxisLabel,
      xAxisSuffix,
      yAxisSuffix,
      xAxisLabel,
      yAxisLabel,
      decimals,
      unit,
    } = options ?? {}

    const defaultFormat = { decimals, unit }
    const xValue = valueToMagnitude({ ...defaultFormat, value: this.x })
    const yValue = valueToMagnitude({ ...defaultFormat, value: this.y })

    const xInfo = disableXAxisLabel
      ? ''
      : `<b>${xAxisLabel ?? ''}:</b> ${xValue}${xAxisSuffix ?? ''}`
    const yInfo = `<b>${yAxisLabel ?? ''}:</b> ${yValue}${yAxisSuffix ?? ''}`
    return `
      <span>● </span><span>${this?.series?.name ?? ''}</span><br/>
      ${xInfo}<br/>
      ${yInfo}<br/>
      ${this?.point?.label ?? ''}
    `
  }

export const dataLabelsFormatter = function () {
  // @ts-ignore
  return this.point.name
}

export const lastPointLabelFormatter = (options?: {
  decimals?: number
  unit?: string
}) =>
  function (this: { series: { xAxis: any }; x: number; y: number }) {
    const { decimals, unit } = options ?? {}
    return this.x === this.series.xAxis.getExtremes().dataMax
      ? valueToMagnitude({ decimals, unit, value: this.y })
      : null
  }

const MAGNITUDES = [
  { value: 1, symbol: '' },
  { value: 1e3, symbol: ' k' },
  { value: 1e6, symbol: ' mi' },
  { value: 1e9, symbol: ' bi' },
  { value: 1e12, symbol: ' tri' },
  { value: 1e15, symbol: ' qua' },
  { value: 1e18, symbol: ' qui' },
]

const getMagnitude = (value: number, magnitudes: Magnitude[]) => {
  let idx
  for (idx = magnitudes.length - 1; idx > 0; idx--) {
    if (Math.abs(value) >= magnitudes[idx].value) {
      break
    }
  }
  return magnitudes[idx]
}

const valueToMagnitude = (options: {
  decimals?: number
  unit?: string
  value: number
}) => {
  const { decimals, unit, value } = options
  const magnitude = getMagnitude(value, MAGNITUDES)
  return (
    (value / magnitude.value).toFixed(decimals ?? 2) +
    magnitude.symbol +
    (unit ?? '')
  )
}

export const magnitudePointFormatter = (options?: {
  decimals?: number
  unit?: string
}) =>
  function (this: {
    y: number
    series: {
      name: string
      color: string
    }
  }) {
    const { decimals, unit } = options ?? {}
    return `<span style="color:${this.series.color}">●</span> ${
      this.series.name
    }: <b>${valueToMagnitude({ decimals, unit, value: this.y })} </b><br/>`
  }

export const getDataSources = (sources: DataSource[] | null) => {
  const getFontText = (sourcesLength: number) =>
    `Fonte${sourcesLength > 1 ? 's' : ''}`

  const getHtmlAsText = (sources: DataSource[]) => {
    const getHrefTag = (source: DataSource) =>
      source.href === ''
        ? source.text.replaceAll('Vendor - ', '')
        : `<a
            href="${source.href}"
            target="_blank"
            rel="noopener noreferrer"
          >${source.text}</a>`

    const sourceLabel = getFontText(sources.length)
    const sourcesLinks = sources.map((source) => getHrefTag(source)).join(', ')
    return `${sourceLabel}: ${sourcesLinks}.`
  }

  const loadCallback = (sources: DataSource[]) => (evt: any) => {
    const chart = evt.target
    if (chart.customText) chart.customText.destroy() // Keep label responsive
    chart.customText = chart.renderer
      .text(getHtmlAsText(sources), 0, chart.chartHeight, true)
      .css({
        color: '#999999',
        fontSize: '0.75rem',
        textAlign: 'right',
      })
      .addClass('w-full')
      .add()
  }

  const jgpSource = [
    { text: 'JGP', href: 'https://www.jgp.com.br', last_capture: null },
  ]

  return {
    chart: { events: { render: loadCallback(sources ?? jgpSource) } },
    credits: { enabled: false },
  }
}
